import * as React from 'react';

import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {Illustration} from 'shells/illustrations/illustration';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Table} from 'shells/table/table';

import type {GetNewsletterEngagementStats_newsletter_editions_edges_node as EditionNode} from 'nutshell-graphql-types';

type Props = {
    editions: EditionNode[];
    isLoading: boolean;
};

export function NewsletterEditionStats(props: Props) {
    const columns = React.useMemo(
        () => [
            {
                Header: 'Name',
                accessor: (edition: EditionNode) => edition,
                dataType: 'linkedName',
                hasLeftBorder: true,
                shouldTruncate: true,
                titleKey: 'name',
            },
            {
                Header: 'Subject',
                accessor: (edition: EditionNode) =>
                    edition.emailTemplate ? edition.emailTemplate.subject : '',
                dataType: 'string',
                minWidth: 75,
                width: 100,
                maxWidth: 110,
            },
            {
                Header: 'Date',
                accessor: (edition: EditionNode) => edition.sendTime,
                dataType: 'timestamp',
                minWidth: 50,
                width: 75,
                maxWidth: 100,
            },
            {
                Header: 'Recipients',
                accessor: (edition: EditionNode) =>
                    edition.engagementStats.totalSentCount.toLocaleString(),
                dataType: 'string',
                minWidth: 55,
                width: 55,
                maxWidth: 75,
            },
            {
                Header: 'Open rate',
                accessor: (edition: EditionNode) => edition.engagementStats.openRate,
                dataType: 'percentage',
                minWidth: 55,
                width: 55,
                maxWidth: 75,
            },
            {
                Header: 'Click rate',
                accessor: (edition: EditionNode) => edition.engagementStats.clickRate,
                dataType: 'percentage',
                minWidth: 55,
                width: 55,
                maxWidth: 75,
            },
            {
                Header: 'Unsubscribe rate',
                accessor: (edition: EditionNode) => edition.engagementStats.unsubscribeRate,
                dataType: 'percentage',
                minWidth: 55,
                width: 75,
                maxWidth: 75,
            },
            {
                Header: 'Bounce rate',
                accessor: (edition: EditionNode) => edition.engagementStats.permanentBounceRate,
                dataType: 'percentage',
                minWidth: 55,
                width: 55,
                maxWidth: 75,
            },
        ],
        []
    );

    return (
        <div className='full-width'>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderTopBorder={true}
                hasHeaderLeftBorder={true}
                hasHeaderRightBorder={true}
                hasHeaderCellBorders={true}
                isCompact={true}
                fullWidth={true}
                hasBorders={true}
                hasCellBorders={true}
                disableFilters={true}
                disableSorting={false}
                // @ts-expect-error
                columns={columns}
                data={props.editions}
                specialStates={[
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage />,
                    },
                    {
                        shouldRender: () => !props.isLoading && props.editions.length === 0,
                        component: (
                            <EmptyStateSmall
                                title='This newsletter has not yet been sent'
                                subtitle='When you send one or more editions, they’ll show up here'
                                icon={
                                    <div style={{width: 80, height: 80}}>
                                        <Illustration
                                            src='cactus-empty-state'
                                            width='100%'
                                            height='100%'
                                        />
                                    </div>
                                }
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}
